import { Grid, Skeleton, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useGetGenreEventsQuery, useLazyGetEventsByTypeQuery } from '../../core/api/eventsSlice';
import Constants from '../../store/constants';
import { SearchInput } from '../../components/searchInput/searchInput';
import { Events } from '../../components/eventsV2/events';
import { DatePicker } from '../../components/datePickerV2/datePicker';
import { HorizontalCarousel } from '../../components/eventCarouselV2/horizontalCarousel';
import { EventGenreType, EventInformationType, EventType } from '../../core/models/events';

const useStyles = makeStyles()((theme: Theme) => ({
  imageCardContent: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0.3) 51.04%, #000000 100%)',
    borderRadius: '0px',
    transition: theme.transitions.create('opacity'),
    objectPosition: '50% 20%',
    width: '100%',
    height: '465px',
    objectFit: 'cover',
  },
  skeletonCategoryImage: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: '100%',
    height: '465px',
  },
  skeletonTicketsFor: {
    height: '134px',
  },
  headingText: {
    textTransform: 'uppercase',
    fontSize: 14,
    lineHeight: '14px',
    cursor: 'default',
  },
  gridSearchInput: {
    paddingBottom: '150px',
    [theme.breakpoints.down('xs')]: {
      margin: '0px 16px',
    },
  },
}));

export const GenreEvents = () => {
  const { genreSlug } = useParams<EventGenreUrlParams>();

  const [selectedLocation, setSelectedLocation] = useState<LocationType>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const eventsCount = 10;

  const { t } = useTranslation();

  const { classes } = useStyles();

  const { data: genre, isLoading: isLoadingGenre } = useGetGenreEventsQuery(genreSlug);
  const [getEventsByTypeRequest, { isLoading: isLoadingEventsByType }] = useLazyGetEventsByTypeQuery();

  const loadEvents = async (
    eventType: EventType,
    search?: string | null,
    date?: Date | null,
    count?: number | null,
    skip?: number
  ): Promise<EventInformationType[]> => {
    const { data } = await getEventsByTypeRequest({
      eventType,
      search,
      date,
      genreId: genre.id,
      count,
      skip,
    });
    return data;
  };

  const loadByDateEvents = async (eventType: EventType, date?: Date, count?: number): Promise<EventGenreType[]> => {
    const { data } = await getEventsByTypeRequest({
      eventType,
      date,
      genreId: genre.id,
      count,
    });
    return data.map((event) => ({
      key: event.slug,
      title: event.title,
      imageUrl: event.posterUrl,
      subTitle: `${dayjs(event.eventDate).format(Constants.DateFormat)}, ${event.city}`,
      date: dayjs(event.eventDate).format(Constants.DateFormat),
      city: event.city,
      domainTypes: event.domainTypes,
    }));
  };

  return (
    <>
      {isLoadingGenre && (
        <span style={{ zIndex: 0 }}>
          <Skeleton animation="wave" variant="rectangular" width="100%" className={classes.skeletonCategoryImage} />
        </span>
      )}

      {!isLoadingGenre && (
        <>
          <span style={{ zIndex: 0 }}>
            <img src={genre.imageUrl} className={classes.imageCardContent} alt={genre.slug} />
          </span>
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={0}
            style={{ marginTop: '60px', zIndex: 1 }}
          >
            <Grid item lg={12} xs={12} sm={12} md={12} xl={12} className={classes.gridSearchInput}>
              <div style={{ marginTop: '10px' }}>
                <SearchInput />
              </div>
            </Grid>

            {selectedLocation?.value && (
              <Events
                title={`события рядом с ${selectedLocation.value}`}
                reloadDependencyValue={selectedLocation.value}
                load={(skip) => loadEvents(EventType.Near, selectedLocation.id, undefined, undefined, skip)}
              />
            )}
            <Grid container style={{ padding: '12px 16px' }}>
              <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
                <Events load={(skip) => loadEvents(EventType.Popular, undefined, undefined, undefined, skip)} title={t('Dashboard.PopularEvents')} />
              </Grid>

              <Grid item lg={12} xs={12} sm={12} md={12} xl={12} style={{ marginBottom: 50 }}>
                <DatePicker selectedDate={selectedDate} onDateChanged={setSelectedDate} />
              </Grid>
              <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
                <HorizontalCarousel
                  isLoading={isLoadingEventsByType}
                  reloadDependencyValue={selectedDate}
                  load={() => loadByDateEvents(EventType.Date, selectedDate, eventsCount)}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

type EventGenreUrlParams = {
  genreSlug: string;
};

type LocationType = {
  id: string;
  value: string;
};
